import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import CommonHeader from '../../util_components/common_header';
import CustomSingleSelect from '../../util_components/custom_single_select';
import NoPermission from '../../util_components/no_permission';
import * as actions from '../../../redux/action_creators';
import AdditionalPaymentsTab from './additional_payments';
import AuditTrailTab from './audit';
import CloseVersionsTab from './close_versions';
import EmailLogsTab from './email_logs';
import RulesEngineTab from './rules_engine';
import NeedsAttentionTab from './needs_attention';
import OverviewTab from './overview';
import PenaltiesTab from './penalties';
import ReportsTab from './reports';
import StubTab from './stub_tab';
import CloseSwitchChip from './close_switch/close_switch_chip';
import useCloseSwitch from './close_switch/use_close_switch';
import { fetch_month_summary, fetch_months } from './api';
import { error_message, is_permission_error, month_label } from './format';
import { with_writable_months } from './additional_payments/params';
import { months_view, tab_badges } from './overview/adapters';
import { parse_needs_attention_deep_link } from './needs_attention/params';
import { ist_day } from './shared/ist';
import { audit_link } from './audit/params';
import { close_versions_badge } from './close_versions/adapters';
import { useCloseVersionsSummary } from './close_versions/use_summary';
import { MONTH_TABS, NO_MONTH_PICKER_TABS, TABS } from './tabs';
import { month_from_search, with_month } from './shared/month_query';
import {
  accept_month_choice,
  parse_overview_deep_link,
  pick_initial_month
} from './overview/params';
import './teacher_payments.css';

/*
 * PROD-7828 — Teacher Payments module shell (/teacher-payments/:tab).
 * Overview (R5); Needs attention (R5b), which also owns the month close/reopen actions; Penalties
 * (R6); Additional payments (R8); Reports, with the audit trail as a hidden tab (R10); Close versions
 * (D111); Rules (R14 P4, retired R10's read-only rules page 2026-10-08 — an old /rules bookmark
 * redirects here). D32: session review is the MUST REVIEW tier of Needs attention, not a tab. The
 * remaining tabs are explicit stubs so no mock data is reachable.
 */
const system_now = () => new Date();

const TeacherPayments = ({ set_notification_variable, now = system_now }) => {
  const history = useHistory();
  const location = useLocation();
  const { tab } = useParams();
  // Slack digest deep link, validated once on mount (params.js); later URL edits don't re-apply it.
  const [deep_link, setDeepLink] = useState(() => parse_overview_deep_link(location.search));
  // R5b digest links: ?view=monthly|daily&pay_month=&tier=&day= — whitelisted once, on mount.
  const [na_deep_link] = useState(() =>
    parse_needs_attention_deep_link(location.search, ist_day(now()))
  );
  // Its view/tier/day seed the tab's first mount only; a later visit opens the default view.
  const [na_link_used, setNaLinkUsed] = useState(false);
  const consume_na_deep_link = useCallback(() => setNaLinkUsed(true), []);
  // D3: the needs-review seed is consumed by the first Overview mount, never re-applied.
  const consume_deep_link = useCallback(
    () => setDeepLink((d) => (d.needs_review_only ? { ...d, needs_review_only: false } : d)),
    []
  );
  // Kuldeep 2026-10-08: the old read-only rules page is retired in favour of the Rules tab; an old
  // bookmark to it still lands somewhere live.
  useEffect(() => {
    if (tab === 'rules') history.replace(`/teacher-payments/rules-engine${location.search}`);
  }, [tab, history, location.search]);

  const active_tab = TABS.some((t) => t.key === tab) ? tab : 'overview';

  const [loading, setLoading] = useState(true);
  const [no_permission, setNoPermission] = useState(false);
  const [months, setMonths] = useState([]);
  // F3: 'loading' | 'ready' | 'empty' (no computed month yet) | 'error' — never an endless spinner.
  const [months_status, setMonthsStatus] = useState('loading');
  const [pay_month, setPayMonth] = useState('');
  const [summary, setSummary] = useState({});
  // Bumped after a close, reopen, issue action or Additional save: the badges and months re-read.
  const [meta_rev, setMetaRev] = useState(0);
  const refresh_meta = useCallback(() => setMetaRev((r) => r + 1), []);
  // The month-close switch chip; a flip bumps switch_rev so Needs attention re-reads close_enabled.
  const {
    data: close_switch,
    reload: reload_close_switch,
    replace: replace_close_switch
  } = useCloseSwitch();
  const [switch_rev, setSwitchRev] = useState(0);

  const is_month_tab = MONTH_TABS.indexOf(active_tab) !== -1;
  const shows_month_picker = NO_MONTH_PICKER_TABS.indexOf(active_tab) === -1;
  const is_additional = active_tab === 'additional-payments';
  // The months read below sees the latest tab, month and clock through refs, not a stale closure.
  const pay_month_ref = useRef(pay_month);
  pay_month_ref.current = pay_month;
  const is_additional_ref = useRef(is_additional);
  is_additional_ref.current = is_additional;
  const now_ref = useRef(now);
  now_ref.current = now;
  // Q15: the Additional tab can write the current and previous IST months before /months lists them.
  const tab_months = useMemo(
    () =>
      is_additional && months_status !== 'loading' ? with_writable_months(months, now()) : months,
    [is_additional, months, months_status, now]
  );
  // A month picked on the Additional tab that another tab does not list falls back to that tab's default.
  const shown_month = accept_month_choice(pay_month, tab_months)
    ? pay_month
    : pick_initial_month(tab_months, '');

  // Every read is grant-gated; a 403 means "no grant", which must render the clean
  // no-permission page instead of an error toast on an empty shell.
  const handle_error = useCallback(
    (e, fallback) => {
      if (is_permission_error(e)) {
        setNoPermission(true);
        return;
      }
      set_notification_variable(true, 'error', error_message(e, fallback));
    },
    [set_notification_variable]
  );

  // The URL's ?month= (or a legacy ?pay_month= deep link) is read once, here, through a ref so a later
  // edit of the address bar by the user never re-seeds the month out from under them.
  const url_month_ref = useRef(month_from_search(location.search));

  useEffect(() => {
    let cancelled = false;
    const refresh = meta_rev > 0;
    const asked_on = pay_month_ref.current;
    fetch_months()
      .then((data) => {
        if (cancelled) return;
        const list = months_view(data).months;
        setMonths(list);
        // A refresh keeps the month on screen, including an Additional-only one while that tab is
        // open (CD-121); only the first load applies the deep link.
        const keepable = is_additional_ref.current
          ? with_writable_months(list, now_ref.current())
          : list;
        // A month picked while this read was in flight is newer than it, so it is never overridden.
        setPayMonth((cur) =>
          cur !== asked_on || accept_month_choice(cur, keepable)
            ? cur
            : pick_initial_month(
                list,
                cur || url_month_ref.current || deep_link.pay_month || na_deep_link.pay_month
              )
        );
        setMonthsStatus(list.length ? 'ready' : 'empty');
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        handle_error(e, 'Failed to load teacher payment months.');
        // A failed refresh leaves the page as it was; only a failed first load has nothing to show.
        if (!refresh) setMonthsStatus('error');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [handle_error, deep_link, na_deep_link, meta_rev]);

  useEffect(() => {
    if (!shown_month) {
      setSummary({}); // D1: no month → no badges from a previous month
      return undefined;
    }
    let cancelled = false;
    fetch_month_summary(shown_month)
      .then((data) => {
        if (!cancelled) setSummary(tab_badges(data));
      })
      .catch((e) => {
        // Badge counts are decoration — never block the page on them.
        if (!cancelled) handle_error(e, 'Failed to load month summary counts.');
      });
    return () => {
      cancelled = true;
    };
  }, [shown_month, handle_error, meta_rev]);

  // D111: one summary read per month (and per meta_rev) badges the tab and feeds it; a failed read
  // only hides the badge, the tab says so itself.
  const close_versions_summary = useCloseVersionsSummary(shown_month, meta_rev);

  // The URL mirrors the shown month (replace, not push: a month change is not a history step) on every
  // tab that has a month picker. A tab with none (rules; audit) neither gains nor loses it, so moving
  // back to a month tab still has it. Tab filter writers (penalties, close_versions) merge into the
  // current search too, so this never gets clobbered by their own history.replace.
  useEffect(() => {
    if (!shows_month_picker) return;
    // A Back/Forward to another listed month is adopted by the effect below, not overwritten here.
    const url_month = month_from_search(location.search);
    if (
      history.action === 'POP' &&
      url_month &&
      url_month !== shown_month &&
      accept_month_choice(url_month, tab_months)
    )
      return;
    const next_qs = with_month(location.search, shown_month);
    if (location.search.replace(/^\?/, '') !== next_qs) {
      history.replace({ pathname: location.pathname, search: next_qs ? `?${next_qs}` : '' });
    }
  }, [shows_month_picker, shown_month, history, location.pathname, location.search, tab_months]);

  // Back/Forward (POP) lands on an entry whose ?month= may differ from the month in state; adopt it, else
  // the mirror effect above would rewrite the URL back and undo the navigation.
  useEffect(() => {
    if (history.action !== 'POP') return;
    const m = month_from_search(location.search);
    if (m && m !== pay_month_ref.current && accept_month_choice(m, tab_months)) setPayMonth(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Tab switches keep the month (a bare history.push would otherwise drop every query param).
  const go_tab = useCallback(
    (key) =>
      history.push({
        pathname: `/teacher-payments/${key}`,
        search: shown_month ? `?${with_month('', shown_month)}` : ''
      }),
    [history, shown_month]
  );

  const notify = useCallback(
    (type, message) => set_notification_variable(true, type, message),
    [set_notification_variable]
  );

  const on_switch_changed = useCallback(
    (data) => {
      replace_close_switch(data);
      setSwitchRev((r) => r + 1);
      if (data && typeof data.close_enabled === 'boolean') {
        notify('success', `Month close is now ${data.close_enabled ? 'ON' : 'OFF'}.`);
      }
    },
    [replace_close_switch, notify]
  );

  // D1: only a month this tab lists is accepted (the empty "Select a month" option never is).
  const choose_month = (value) => {
    if (accept_month_choice(value, tab_months)) setPayMonth(value);
  };

  const selected_month = useMemo(
    () => tab_months.find((m) => m.pay_month === shown_month) || {},
    [tab_months, shown_month]
  );

  if (no_permission) {
    return <NoPermission />;
  }

  const month_options = tab_months.map((m) => ({ value: m.pay_month, label: m.label }));

  const active = TABS.find((t) => t.key === active_tab) || TABS[0];

  return (
    <CommonHeader loading={loading} title="Teacher Payments">
      <div className="tp-page">
        <div className="tp-header">
          <span className="tp-title">Teacher Payments</span>
          {shows_month_picker && selected_month.badge && selected_month.badge.key ? (
            <span
              className={`tp-lifecycle tp-lifecycle-${selected_month.badge.key}`}
              title="Lifecycle of this month across its teachers; close and reopen them under Needs attention"
            >
              {selected_month.badge.label}
            </span>
          ) : null}
          <CloseSwitchChip
            data={close_switch}
            on_changed={on_switch_changed}
            on_stale={reload_close_switch}
          />
          {shows_month_picker ? (
            <div style={{ width: '200px' }}>
              <CustomSingleSelect
                ariaLabel="Payment month"
                label="Payment month"
                options={month_options}
                value={shown_month}
                defaultOption="Select a month"
                onChange={(e) => choose_month(e.target.value)}
              />
            </div>
          ) : null}
        </div>

        <div className="tp-tabs">
          {TABS.filter((t) => !t.hidden).map((t) => {
            const badge =
              (t.key === 'close-versions'
                ? close_versions_badge(close_versions_summary.data)
                : summary[t.key]) || 0;
            return (
              <button
                key={t.key}
                type="button"
                className={`tp-tab ${active_tab === t.key ? 'on' : ''}`}
                onClick={() => go_tab(t.key)}
              >
                {t.label}
                {badge ? <span className="tp-tab-ct">{badge}</span> : null}
              </button>
            );
          })}
        </div>

        {is_month_tab && !is_additional && months_status === 'empty' ? (
          <div className="tp-empty">
            No payment months yet. The module fills in once the first month has been computed.
          </div>
        ) : null}
        {is_month_tab && months_status === 'error' ? (
          <div className="tp-banner tp-banner-error">
            Could not load the payment months. Reload the page to try again.
          </div>
        ) : null}
        {active_tab === 'overview' && months_status === 'ready' && shown_month ? (
          <OverviewTab
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            initial_needs_review={deep_link.needs_review_only}
            on_deep_link_consumed={consume_deep_link}
            on_open_needs_attention={() => go_tab('needs-attention')}
            on_error={handle_error}
          />
        ) : null}
        {active_tab === 'needs-attention' && months_status === 'ready' && shown_month ? (
          <NeedsAttentionTab
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            deep_link={na_link_used ? {} : na_deep_link}
            on_deep_link_consumed={consume_na_deep_link}
            months={months}
            on_month={choose_month}
            on_changed={refresh_meta}
            on_error={handle_error}
            now={now}
            switch_rev={switch_rev}
            on_summary_loaded={reload_close_switch}
          />
        ) : null}
        {active_tab === 'penalties' && months_status === 'ready' && shown_month ? (
          <PenaltiesTab
            key={shown_month}
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            month_badge={selected_month.badge}
            on_error={handle_error}
            on_changed={refresh_meta}
          />
        ) : null}
        {is_additional && months_status !== 'loading' && shown_month ? (
          <AdditionalPaymentsTab
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            month_badge={selected_month.badge}
            now={now}
            on_error={handle_error}
            on_notify={notify}
            on_month_change={choose_month}
            on_changed={refresh_meta}
          />
        ) : null}
        {active_tab === 'reports' && months_status === 'ready' && shown_month ? (
          <ReportsTab
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            on_error={handle_error}
            on_open_audit={() => history.push(audit_link(shown_month, ''))}
          />
        ) : null}
        {active_tab === 'close-versions' && months_status === 'ready' && shown_month ? (
          <CloseVersionsTab
            key={shown_month}
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            summary={close_versions_summary}
            on_error={handle_error}
            on_changed={refresh_meta}
            on_open_needs_attention={() => go_tab('needs-attention')}
          />
        ) : null}
        {active_tab === 'email-logs' && months_status === 'ready' && shown_month ? (
          <EmailLogsTab
            key={shown_month}
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            on_error={handle_error}
          />
        ) : null}
        {/* The audit trail also opens on a teacher-only link, so it waits only for the months read. */}
        {active_tab === 'audit' && months_status !== 'loading' ? (
          <AuditTrailTab pay_month={shown_month} months={months} on_error={handle_error} />
        ) : null}
        {active_tab === 'rules-engine' ? (
          <RulesEngineTab on_error={handle_error} now={now} />
        ) : null}
        {active.release ? <StubTab label={active.label} release={active.release} /> : null}
      </div>
    </CommonHeader>
  );
};

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

export default connect(null, mapDispatchToProps)(TeacherPayments);
